import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield, Eye, FileText, AlertTriangle, ClipboardCheck, Building2,
  Clock, TrendingUp, Lock, Calendar, BarChart3, Activity, Scale,
  Users, PieChart, AlertOctagon, CheckCircle2, History
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell
} from 'recharts';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

/**
 * RegulatorDashboardPage - Oversight dashboard for regulatory authorities
 * Provides read-only access to safety data within their jurisdiction
 */
const RegulatorDashboardPage = () => {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userContext, setUserContext] = useState(null);
  const [oversight, setOversight] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [audits, setAudits] = useState([]);
  const [spis, setSPIs] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  const fetchData = useCallback(async () => {
    if (!api) return;
    
    try {
      setLoading(true);
      const [contextRes, incidentsRes, auditsRes, spisRes, orgsRes] = await Promise.all([
        api.get('/multiorg/context/me'),
        api.get('/incidents?limit=50').catch(() => ({ data: [] })),
        api.get('/audits?limit=20').catch(() => ({ data: [] })),
        api.get('/spis').catch(() => ({ data: [] })),
        api.get('/multiorg/organizations').catch(() => ({ data: [] })),
      ]);
      
      setUserContext(contextRes.data);
      setIncidents(incidentsRes.data || []);
      setAudits(auditsRes.data || []);
      setSPIs(spisRes.data || []);
      setOrganizations(orgsRes.data || []);
      
      // Get oversight config if user is regulator
      if (contextRes.data?.is_regulator) {
        try {
          const oversightRes = await api.get(`/multiorg/regulator-oversight/${user.id}`);
          setOversight(oversightRes.data);
        } catch (e) {
          console.log('No oversight config found');
        }
      }
    } catch (error) {
      console.error('Error fetching regulator data:', error);
      toast.error('Failed to load regulator dashboard data');
    } finally {
      setLoading(false);
    }
  }, [api, user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check if user is a regulator
  const isRegulator = userContext?.is_regulator;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  if (!isRegulator) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Lock className="w-16 h-16 text-slate-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Regulator Dashboard</h2>
        <p className="text-slate-400">
          This dashboard is only accessible to regulatory authority users.
        </p>
      </div>
    );
  }

  // Calculate statistics
  const totalIncidents = incidents.length;
  const criticalIncidents = incidents.filter(i => i.severity === 'critical').length;
  const openAudits = audits.filter(a => a.status !== 'completed').length;
  const organizationsInScope = oversight?.organizations_in_scope?.length || 0;

  // Incident trend data (mock for visualization)
  const incidentTrendData = [
    { month: 'Jan', incidents: 12 },
    { month: 'Feb', incidents: 15 },
    { month: 'Mar', incidents: 11 },
    { month: 'Apr', incidents: 18 },
    { month: 'May', incidents: 14 },
    { month: 'Jun', incidents: 10 },
  ];

  // Severity distribution
  const severityData = [
    { name: 'Critical', value: criticalIncidents, color: '#ef4444' },
    { name: 'Major', value: incidents.filter(i => i.severity === 'major').length, color: '#f97316' },
    { name: 'Minor', value: incidents.filter(i => i.severity === 'minor').length, color: '#eab308' },
    { name: 'Negligible', value: incidents.filter(i => i.severity === 'negligible').length, color: '#22c55e' },
  ];

  // Access scope labels
  const scopeLabels = {
    full_read: 'Full Read Access',
    compliance_only: 'Compliance Data Only',
    audit_only: 'Audit Data Only',
    incident_only: 'Incident Data Only',
  };

  return (
    <div className="space-y-6" data-testid="regulator-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Scale className="w-7 h-7 text-red-400" />
            {t('regulator.title')}
          </h1>
          <p className="text-slate-400 mt-1">
            {oversight?.authority_name || oversight?.authority || t('regulator.overview')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-red-500/20 text-red-400 px-3 py-1">
            <Eye className="w-4 h-4 mr-1" />
            Read-Only Access
          </Badge>
          <Badge variant="outline" className="px-3 py-1">
            {scopeLabels[oversight?.access_scope] || 'Limited Scope'}
          </Badge>
        </div>
      </div>

      {/* Just Culture Notice */}
      <Card className="bg-amber-900/20 border-amber-600/30">
        <CardContent className="p-4 flex items-start gap-3">
          <Shield className="w-6 h-6 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-300">Just Culture Protection Active</h3>
            <p className="text-amber-200/80 text-sm mt-1">
              Anonymous and confidential reports are protected. Reporter identities are not disclosed 
              to maintain a non-punitive reporting culture. Only aggregate safety data is available.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Organizations in Scope</p>
                <p className="text-2xl font-bold">{organizationsInScope}</p>
              </div>
              <Building2 className="w-8 h-8 text-sky-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Incidents</p>
                <p className="text-2xl font-bold">{totalIncidents}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Critical Incidents</p>
                <p className="text-2xl font-bold text-red-400">{criticalIncidents}</p>
              </div>
              <AlertOctagon className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Open Audits</p>
                <p className="text-2xl font-bold">{openAudits}</p>
              </div>
              <ClipboardCheck className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Access Info Card */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="w-5 h-5 text-purple-400" />
            Access Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-slate-400 text-sm">Historical Access</p>
              <p className="font-semibold">{oversight?.historical_access_days || 365} days</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm">Access Count</p>
              <p className="font-semibold">{oversight?.access_count || 0} views</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm">Can Export</p>
              <p className="font-semibold">{oversight?.can_export_data ? 'Yes' : 'No'}</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm">Last Accessed</p>
              <p className="font-semibold">
                {oversight?.last_accessed 
                  ? new Date(oversight.last_accessed).toLocaleDateString()
                  : 'N/A'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Tabs defaultValue="overview">
        <TabsList className="bg-slate-800">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="incidents">Incidents</TabsTrigger>
          <TabsTrigger value="audits">Audits</TabsTrigger>
          <TabsTrigger value="spis">Safety Indicators</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Incident Trend Chart */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle>Incident Trend (6 Months)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={incidentTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="month" stroke="#94a3b8" />
                      <YAxis stroke="#94a3b8" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1e293b', 
                          border: '1px solid #334155' 
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="incidents" 
                        stroke="#38bdf8" 
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Severity Distribution */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle>Incident Severity Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px] flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={severityData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {severityData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1e293b', 
                          border: '1px solid #334155' 
                        }}
                      />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-4 mt-2">
                  {severityData.map(item => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-sm text-slate-400">{item.name}: {item.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Organizations in Scope */}
            <Card className="bg-slate-900 border-slate-800 lg:col-span-2">
              <CardHeader>
                <CardTitle>Organizations Under Oversight</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {organizations.slice(0, 8).map(org => (
                    <div 
                      key={org.id}
                      className="p-3 bg-slate-800 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-sky-400" />
                        <span className="font-medium truncate">{org.name}</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{org.code}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Incidents Tab */}
        <TabsContent value="incidents" className="mt-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle>Incident Reports</CardTitle>
              <CardDescription>
                Occurrence reports within your jurisdiction (anonymous reporter info protected)
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead>Report #</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {incidents.slice(0, 20).map(incident => (
                    <TableRow key={incident.id} className="border-slate-800">
                      <TableCell className="font-mono">{incident.report_number}</TableCell>
                      <TableCell>{incident.title}</TableCell>
                      <TableCell>
                        {new Date(incident.occurrence_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{incident.category}</TableCell>
                      <TableCell>
                        <Badge className={
                          incident.severity === 'critical' ? 'bg-red-500' :
                          incident.severity === 'major' ? 'bg-orange-500' :
                          incident.severity === 'minor' ? 'bg-yellow-500' :
                          'bg-slate-500'
                        }>
                          {incident.severity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{incident.status}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {incidents.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-400 py-8">
                        No incidents found in your oversight scope
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audits Tab */}
        <TabsContent value="audits" className="mt-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle>Audit Results</CardTitle>
              <CardDescription>
                Audit findings and compliance status
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead>Audit #</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Findings</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {audits.map(audit => (
                    <TableRow key={audit.id} className="border-slate-800">
                      <TableCell className="font-mono">{audit.audit_number}</TableCell>
                      <TableCell>{audit.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{audit.audit_type}</Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(audit.planned_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{audit.findings?.length || 0}</TableCell>
                      <TableCell>
                        <Badge className={
                          audit.status === 'completed' ? 'bg-green-500' :
                          audit.status === 'in_progress' ? 'bg-blue-500' :
                          'bg-amber-500'
                        }>
                          {audit.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {audits.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-400 py-8">
                        No audits found in your oversight scope
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SPIs Tab */}
        <TabsContent value="spis" className="mt-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle>Safety Performance Indicators</CardTitle>
              <CardDescription>
                Key safety metrics and trends
              </CardDescription>
            </CardHeader>
            <CardContent>
              {spis.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No SPIs configured</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {spis.map(spi => (
                    <div 
                      key={spi.id}
                      className="p-4 bg-slate-800 rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">{spi.name}</h4>
                        <Badge className={
                          spi.status === 'on_target' ? 'bg-green-500' :
                          spi.status === 'warning' ? 'bg-amber-500' :
                          'bg-red-500'
                        }>
                          {spi.status || 'N/A'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <Progress 
                            value={spi.current_value ? (spi.current_value / spi.target_value) * 100 : 0} 
                            className="h-2"
                          />
                        </div>
                        <span className="text-sm font-mono">
                          {spi.current_value || 0} / {spi.target_value}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RegulatorDashboardPage;
